// src/services/authService.ts
import { AxiosResponse } from 'axios';
import apiClient, { setAuthToken } from './apiClient';
import { lockRefresh, getNewAccess } from './refreshLock';
import axios from 'axios';


const API_BASE_URL = process.env.REACT_APP_API_URL;

/**
 * ユーザー登録 API
 * POST: /users/register/
 * @param data { email: string, username?: string }
 */
export const registerUser = async (data: {
  email: string;
  username?: string;
}): Promise<AxiosResponse> => {
  return apiClient.post('/users/register/', data);
};

// 1) メールアドレス登録（コード送信）
export const registerEmail = async (data: { email: string }) =>
  apiClient.post('/users/register-email/', data);

// 2) 6桁コード検証
export const verifyOTP = async (data: { email: string; code: string }) =>
  apiClient.post('/users/register-verify/', data);

// 3) 本登録完了（ユーザー名＋パスワード設定）
export const completeRegistration = async (data: {
  token: string;
  username: string;
  password: string;
}) => apiClient.post('/users/register-complete/', data);

/**
 * パスワードリセットメール送信
 * POST: /users/password-reset/
 */
export const requestPasswordReset = async (data: { email: string }) =>
  apiClient.post('/users/password-reset/', data);

/**
 * パスワードリセット確定（トークン＋新パスワード設定）
 * POST: /users/password-reset-confirm/
 */
export const confirmPasswordReset = async (data: {
  token: string;
  new_password: string;
  confirm_password: string;
}) => apiClient.post('/users/password-reset-confirm/', data);

/**
 * アクティベーション（アカウント有効化） API
 * POST: /users/activate/
 * @param data { token: string, password: string }
 */
export const activateAccount = async (data: {
  token: string;
  password: string;
}): Promise<AxiosResponse> => {
  return apiClient.post('/users/activate/', data);
};

/**
 * ログイン API
 * POST: /users/login/
 * @param data { email: string, password: string }
 *
 * ログイン成功時、レスポンスから access, refresh トークンを localStorage に保存し、
 * また axios の共通インスタンスに Authorization ヘッダーをセットして返却します。
 */
export const login = async (data: {
  email: string;
  password: string;
  code?: string;
}): Promise<AxiosResponse> => {
  const response = await apiClient.post('/users/login/', data);
  if (response.data && response.data.access && response.data.refresh) {
    const { access, refresh } = response.data;
    localStorage.setItem('auth_token', access);
    localStorage.setItem('refresh_token', refresh);
    setAuthToken(access);
  }
  return response;
};

/**
 * refreshToken API
 * POST: /token/refresh/
 *
 * リフレッシュトークンを元に、新しいアクセストークンを取得します。
 */
const rawClient = axios.create({ baseURL: API_BASE_URL });

export const refreshToken = async (): Promise<string> => {
  /** ① すでにリフレッシュ中なら「その結果」を待って返す */
  const ongoing = getNewAccess();
  if (ongoing) return ongoing;

  /** ② 自分がリフレッシュの先頭なら Promise を生成してロック */
  const p = (async () => {
    const old = localStorage.getItem('refresh_token');
    if (!old) throw new Error('refresh token missing');

    const { data } = await rawClient.post('/users/token/refresh/', {
      refresh: old,
    });

    // 成功したら新しいトークンを保存
    localStorage.setItem('auth_token', data.access);
    localStorage.setItem('refresh_token', data.refresh);
    setAuthToken(data.access);
    return data.access;            // ← access を返す
  })();

  return lockRefresh(p);           // ★ ここがポイント
};

/**
 * ログアウト処理
 * ローカルストレージからトークンを削除し、axios インスタンスの認証ヘッダーもクリアします。
 */
export const logout = (): void => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  setAuthToken(null);
};

/**
 * パスワード変更 API
 * POST: /users/me/change-password/
 * @param data { old_password: string, new_password: string }
 */
export const changePassword = async (data: {
  old_password: string;
  new_password: string;
}): Promise<AxiosResponse> => {
  return apiClient.post('/users/me/change-password/', data);
};
